/**
 * FeatureLockedOverlay Component
 * 
 * Displays a premium locked state overlay when a feature is not available.
 * Shows lock icon, tier requirement message, and upgrade button.
 */

import React from 'react';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useNavigate } from 'react-router-dom';

export function FeatureLockedOverlay({ 
  feature,
  featureLabel,
  requiredTier = 'professional',
  language = 'en',
  className = '',
  children,
  compact = false
}) {
  const navigate = useNavigate();
  
  const t = {
    en: {
      locked: 'Premium Feature',
      requires: 'This feature requires',
      tier: requiredTier === 'professional' ? 'Professional' : 'Enterprise',
      upgrade: 'Upgrade',
      viewPlans: 'View Plans',
      free: 'Free',
      professional: 'Professional',
      enterprise: 'Enterprise'
    },
    cs: {
      locked: 'Prémiová funkce',
      requires: 'Tato funkce vyžaduje',
      tier: requiredTier === 'professional' ? 'Professional' : 'Enterprise',
      upgrade: 'Upgradovat',
      viewPlans: 'Zobrazit plány',
      free: 'Zdarma',
      professional: 'Professional',
      enterprise: 'Enterprise'
    }
  };
  
  const labels = t[language] || t.en;
  
  const tierColors = {
    free: 'bg-slate-100 text-slate-600 border-slate-300',
    professional: 'bg-blue-50 text-blue-700 border-blue-300',
    enterprise: 'bg-purple-50 text-purple-700 border-purple-300'
  };

  if (compact) {
    return (
      <div className={`relative ${className}`}>
        {/* Blurred/disabled content */}
        <div className="opacity-40 pointer-events-none select-none blur-[1px]">
          {children}
        </div>
        
        {/* Compact overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-lg">
          <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg shadow-sm border border-slate-200">
            <Lock className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">
              {labels.requires} <strong>{labels.tier}</strong>
            </span>
            <Button 
              size="sm" 
              variant="outline"
              className="h-7 text-xs gap-1"
              onClick={() => navigate('/subscription')}
            >
              {labels.upgrade}
              <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Blurred/disabled content */}
      <div className="opacity-30 pointer-events-none select-none blur-[2px]">
        {children}
      </div>
      
      {/* Full overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-white/80 via-white/90 to-white/80 backdrop-blur-sm rounded-lg p-6">
        {/* Lock icon with glow */}
        <div className="relative mb-4">
          <div className="absolute inset-0 bg-amber-400/20 rounded-full blur-xl"></div>
          <div className="relative w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-50 rounded-full flex items-center justify-center border border-amber-200 shadow-lg">
            <Lock className="h-8 w-8 text-amber-600" />
          </div>
        </div>
        
        {/* Badge */}
        <Badge 
          variant="outline" 
          className={`mb-3 ${tierColors[requiredTier]}`}
        >
          <Sparkles className="h-3 w-3 mr-1" />
          {labels.locked}
        </Badge>
        
        {/* Message */}
        <p className="text-center text-slate-600 mb-1">
          {labels.requires}
        </p>
        <p className="text-center font-semibold text-lg text-slate-800 mb-4">
          {labels.tier}
        </p>
        
        {/* Feature label if provided */}
        {featureLabel && (
          <p className="text-sm text-slate-500 mb-4 text-center max-w-xs">
            "{featureLabel}"
          </p>
        )}
        
        {/* Upgrade button */}
        <Button 
          className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md"
          onClick={() => navigate('/subscription')}
        >
          <Sparkles className="h-4 w-4" />
          {labels.upgrade}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * FeatureGate Component
 * 
 * Conditionally renders children or locked overlay based on feature access.
 */
export function FeatureGate({ 
  feature, 
  featureLabel,
  hasAccess, 
  requiredTier = 'professional',
  language = 'en',
  compact = false,
  children 
}) {
  if (hasAccess) {
    return <>{children}</>;
  }
  
  return (
    <FeatureLockedOverlay
      feature={feature}
      featureLabel={featureLabel}
      requiredTier={requiredTier}
      language={language}
      compact={compact}
    >
      {children}
    </FeatureLockedOverlay>
  );
}

export default FeatureLockedOverlay;
